import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment'
import { debounce } from 'throttle-debounce'

import Icon from './Icon'
import '../styles/timePicker.scss'
import ClickListener from '../utils/useClickOutside';
import { runByFeature } from '../utils/runByFeature';

const getHours = (format, timeInterval) => {
  const currentTime = moment().hour(0).minute(0)

  return new Array(24*60/timeInterval).fill().map(() => {
    const formattedTime = currentTime.format(format)
    currentTime.add(timeInterval, "m")

    return formattedTime
  })
}

const compareTimeForSelect = (inputValue, time) => inputValue && moment(`01/01/1970 ${time}`).isSame(`01/01/1970 ${inputValue}`)
const closetTimeItem = (mTime, interval) => Math.floor(( mTime.hours() * 60 + mTime.minutes()) / interval)

const NavigateDirection = {
  ArrowUp: -1,
  ArrowDown: 1
}

const isPopUpToTop = (inputRef, menuRef ) => {
  if (!menuRef.current || !inputRef.current) {
    return false
  }

  const input = inputRef.current.getBoundingClientRect()
  const menu = menuRef.current.getBoundingClientRect()

  const windowHeight = (window.innerHeight || document.documentElement.clientHeight);
  return windowHeight - (input.bottom + 20) < menu.height
}

const TimePicker = (props) => {
  const { format, timeInterval, onBlur, onChange, t, value, disabled, stateValue, features } = props

  const [inputValue, setInputValue] = useState('')
  const [selectedValue, setSelectedValue] = useState(inputValue)
  const [lastEvent, setLastEvent] = useState('')
  const [prevValueState, setPrevValueState] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isTimeValid, setIsTimeValid] = useState(true)
  const [isDirectionTop, setIsDirectionTop] = useState(false)

  const itemRefs = new Array(24*60/timeInterval).fill().map(() => useRef())
  const scrollAreaRef = useRef(null)
  const popUpRef = useRef(null)

  const repositionHandler = debounce(150, true, () => setIsDirectionTop(isPopUpToTop(popUpRef, scrollAreaRef)))

  const getInitialTime = (value, format, features) => {
    return runByFeature(features, {
      newTimezoneModel: () => {
        const mTime = moment(value, ['YYYY-MM-DDTHH:mm:ss','YYYY-MM-DDTHH:mm:ssZ', 'HH:mm', 'HH:mm:ss'], true)
        return value && mTime.isValid() ? mTime.format(format) : ''
      },
      otherwise: () => {
        const mDate = moment(value, 'YYYY-MM-DD', true)
        const mTime = moment(value, ['YYYY-MM-DDTHH:mm:ss','YYYY-MM-DDTHH:mm:ssZ', 'HH:mm', 'HH:mm:ss'], true)

        if(mDate.isValid() && stateValue) {
          return moment(`1/1/1970 ${stateValue}`).format(format)
        }
        if(mTime.isValid()) {
          return mTime.format(format)
        }
        return ''
      }
    })
  }

  useEffect(() => repositionHandler(), [])

  useEffect(() => {
    setInputValue(getInitialTime(value, format, features))
    setLastEvent('')
    setIsOpen(false)
  }, [value])

  useEffect(() => {
    window.addEventListener('resize', repositionHandler)
    window.addEventListener('scroll', repositionHandler)

    return () => {
      removeEventListener('resize', repositionHandler)
      removeEventListener('scroll', repositionHandler)
    }
  },[])

  useEffect(() => {
    if (isOpen) {
      const mTime = moment(`1/1/1970 ${inputValue || selectedValue}`)
      scrollToClosetEntry(mTime)
    }
  }, [isOpen])

  const onKeyUp = (e) => (e.key === 'Escape' || e.key === 'Enter') && setIsOpen(false)

  const scrollToClosetEntry = (mTime) => {
    const closetEntry = Math.max(0, closetTimeItem(mTime, timeInterval) - 1)

    if(scrollAreaRef && scrollAreaRef.current && itemRefs[closetEntry] && itemRefs[closetEntry].current) {
      scrollAreaRef.current.scrollTo(0, itemRefs[closetEntry].current.offsetTop - 90)
    }
  }

  const onListSelect = (time) => {
    setLastEvent('DropDown')
    setSelectedValue(time)
    setInputValue(time)
    onChange && onChange(time, true)
    setIsTimeValid(true)
    setIsOpen(false)
    popUpRef.current.focus()
  }

  const onInputChange = (e) => {
    setPrevValueState(inputValue)
    const prevValueStateInn = inputValue
    if(e.target.value) {
      setLastEvent('Input')
      setInputValue(e.target.value)

      setIsOpen(true)
      const mTime = moment(`1/1/1970 ${e.target.value}`)

      if (mTime.isValid()) {
        setSelectedValue(e.target.value)
        scrollToClosetEntry(mTime)
        onChange && onChange(mTime.format(format))
        setIsTimeValid(true)
      } else {
        setIsTimeValid(false)
      }
    } else {
      setInputValue('')
      prevValueStateInn !== '' && e.target.value === '' && onChange && onChange('', true)
      setSelectedValue('')
      setIsTimeValid(true)
    }
  }

  const handleOnBlur = () => {
    setIsOpen(false)
    if (!isTimeValid) {
      const mPrevValueState = moment(`1/1/1970 ${prevValueState}`)
      setIsTimeValid(mPrevValueState.isValid())
      setInputValue(mPrevValueState.isValid() ? prevValueState : '')
    }
    onBlur()
  }

  const handleInputOnBlur = (e) => {
    if (e.target.value && lastEvent === 'Input') {
      const mTime = moment(`1/1/1970 ${e.target.value}`)
      if (mTime.isValid()) {
        setInputValue(mTime.format(format))
        scrollToClosetEntry(mTime)
        onChange && onChange(mTime.format(format), true)
        setIsOpen(false)
        setIsTimeValid(true)
      } else {
        setIsTimeValid(false)
      }
    }
  }

  const onItemsNavigation = (e) => {
    const mSelectedTime = moment(`1/1/1970 ${selectedValue}`)
    const direction = NavigateDirection[e.key]

    if (direction) {
      const mNewSelectedValue = mSelectedTime.add(timeInterval * direction, 'minute')
      scrollToClosetEntry(mNewSelectedValue)
      const newSelectedValue = mNewSelectedValue.format(format)
      setSelectedValue(newSelectedValue)
      setInputValue(newSelectedValue)
      setLastEvent('Input')
    }
    if (e.key === 'Enter' || e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const onListMouseDown = (e) => {
    e.preventDefault()
  }

  return (
      <div className="time-picker-wrapper react-datepicker-wrapper">
        <div className="time-picker-input-wrapper">
          <input
            placeholder={t('ducs.set_time')}
            className="time-picker-input"
            type="text"
            onChange={onInputChange}
            value={inputValue}
            onClick={() => setIsOpen(true)}
            onKeyUp={onKeyUp}
            onBlur={handleInputOnBlur}
            disabled={disabled}
            ref={popUpRef}
          />
          <Icon
            name="clock.svg"
            width="20px"
            height="100%"
            classes="datepicker-icon"
            onClick={() => setIsOpen(true)}
          />
        </div>
        {isOpen && <div
          className={`time-picker-select-wrapper ${isDirectionTop ? 'open-to-top' : ''} ${isOpen ? 'open': 'close'}`}
          ref={scrollAreaRef}
        >
          <ClickListener trigger={() => handleOnBlur()} >
            <div className="time-picker-select-title">
              {t('ducs.set_date')}
              <Icon
                name="closeBig.svg"
                width="20px"
                height="100%"
                classes='datepicker-icon'
                onClick={() => setIsOpen(true)}
              />
            </div>
            <ul className="time-picker-select"  tabIndex={0} onKeyUp={onItemsNavigation}>
              {getHours(format, timeInterval).map((time, idx) => {
                return (
                  <li
                    role="option"
                    ref={itemRefs[idx]}
                    onClick={() => onListSelect(time)}
                    onMouseDown={onListMouseDown}
                    className={`${compareTimeForSelect(inputValue, time) || selectedValue === time ? 'selected' : ''} time-picker-select-item`}
                    aria-selected={compareTimeForSelect(inputValue, time) || selectedValue === time}
                    key={time}
                  >{time}</li>
                )
              })}
            </ul>
          </ClickListener>
        </div>}
      </div>
  );
};

export default TimePicker;
